import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useDataSourceSegregate } from "hooks/useDataSourceSegregate";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { pluralize } from "utils/pluralize";
import { AccountCardsSection, TabEmptyState } from "../DataSourcesList";
import { BillComCard } from "./BillComCard";
import { getDisconnectedMessage } from "utils/bankNameAcc";

export const APandAR = ({
  groupId,
  onOpenConnectionModal,
  onOpenSubscriptionModal,
}: {
  groupId: string;
  onOpenConnectionModal: () => void;
  onOpenSubscriptionModal: () => void;
}) => {
  const entityId = useCurrentEntityId();

  const { data: apArConnections } = useGetEntityBanksQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
      type: "AP_AR",
    },
    { skip: !groupId || !entityId }
  );

  const { bankConnections } = useDataSourceSegregate(apArConnections!);

  const accounts = apArConnections?.accounts || [];

  const accountsByBank = accounts.reduce((acc, account) => {
    const name = account.bank_brand.name;
    if (!acc[name]) {
      acc[name] = [];
    }
    acc[name].push(account);
    return acc;
  }, {} as Record<string, typeof accounts>);

  const hasConnections = bankConnections.length > 0;

  return (
    <div className="t-overflow-y-auto t-py-3 t-px-2.5">
      {hasConnections ? (
        <AccountCardsSection>
          {bankConnections.map((connection) => {
            const connectionAccounts = accountsByBank[connection.name] || [];
            const totalBankConnected = connectionAccounts.filter(
              (account) => account.is_bank_connected
            ).length;
            const totalBankDisconnected = connectionAccounts.filter(
              (account) => !account.is_bank_connected
            ).length;
            const disconnectedMessage = getDisconnectedMessage(connection);
            return (
              <BillComCard
                key={connection.uuid}
                name={connection.name}
                description={
                  <>
                    {totalBankConnected > 0 && (
                      <div className="t-text-dark_green t-text-body-md t-lowercase">
                        {pluralize(
                          totalBankConnected,
                          "account connected",
                          "accounts connected"
                        )}
                      </div>
                    )}
                    {totalBankDisconnected > 0 && (
                      <div className="t-text-red-70 t-text-body-md t-lowercase">
                        {disconnectedMessage}
                      </div>
                    )}
                  </>
                }
                connection={connection}
              />
            );
          })}
        </AccountCardsSection>
      ) : (
        <TabEmptyState
          title="No AP or AR sources connected"
          description="Connect your HubSpot or Bill.com account to start syncing your revenue and expense data automatically."
          onOpenConnectionModal={onOpenConnectionModal}
          onOpenSubscriptionModal={onOpenSubscriptionModal}
        />
      )}
    </div>
  );
};
